import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileText, ArrowRight, Loader2, Plus, X, GripVertical, Edit, Check, Save, Trash2, Copy, FileStack, Upload, FileUp, CheckCircle, LayoutTemplate } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api/subbuilder`;

// Drag-Drop Icon Component (square with dashed border and arrow)
function DragDropIcon({ className = "h-4 w-4" }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      {/* Solid square (file) */}
      <rect x="2" y="10" width="10" height="10" rx="1" />
      {/* Dashed square (drop zone) */}
      <rect x="12" y="4" width="10" height="10" rx="1" strokeDasharray="2 2" />
      {/* Arrow pointing from solid to dashed */}
      <path d="M9 13 L15 7" />
      <path d="M15 7 L15 11" />
      <path d="M15 7 L11 7" />
    </svg>
  );
}

// Built-in scope templates configuration (alphabetically ordered by name)
const BUILTIN_SCOPE_TEMPLATES = {
  'aed-cabinets': {
    name: 'AED Cabinets',
    specSection: '10 43 13 Defibrillator Cabinets',
    otherTradesItems: [
      'Wall openings',
      'Blocking',
      'Confirm cabinet will fit in wall depth (depth info is provided in this submittal)',
      'Caulking and/or backer rod'
    ],
    scopeConcerns: [],
    fieldMeasurementItems: [
      'No field measurements are performed on this scope; if needed, trip charge may apply'
    ],
    installationItems: [
      'Has final paint been completed'
    ],
    isBuiltIn: true
  },
  'awl-lockers': {
    name: 'All Welded Lockers',
    specSection: '10 51 12.31 Welded Lockers',
    otherTradesItems: [
      'Backing - located at 8" centerline from bottom and 72" center (not AFF due to base)',
      'Base by others unless otherwise noted',
      'Locks - unless otherwise noted',
      "Power within 50' of locker area for compressor use if building lockers on site"
    ],
    scopeConcerns: [
      'Confirm color selection - frames and doors, all interior colors to be standard for mfg. specifications',
      'Confirm number sequencing. Note: if number plates begin with "00" there will be an additional fee',
      'Confirm no radiant flooring or post tension rods to coordinate floor mounting with - if this is part of your project, x-ray work is by others',
      'Identify locations of ADA required lockers in each elevation',
      'Confirm wall outlets, light switches, strobe lights, etc. will be covered by lockers or slope top',
      'Lock type if applicable'
    ],
    fieldMeasurementItems: [
      'Base is installed; base to be plumb, level and clear of material & debris',
      'Knock down lockers can be ordered without field measurements. Locker quantities may be reduced if opening is smaller than required clear opening. Allow 1/16" growth per frame',
      'Knock down lockers utilize filler panels to fill gaps to walls, filler panels & splices cannot be cut less than 1-1/2" wide',
      'If lockers are recessed with a soffit above, soffit depth must be equal to depth of lockers, soffit height is 1/2" to 2" above lockers finished height'
    ],
    installationItems: [
      'A clear path must be provided for delivery of lockers. Lockers will be pre-built at ABS warehouse',
      'In some cases lockers will be built on site, if that is to happen, ABS needs a clear location near the final locker destination for building lockers',
      'If multiple floor installation; elevator or lift must be provided/available'
    ],
    isBuiltIn: true
  },
  'bike-racks': {
    name: 'Bike Racks',
    specSection: '12 93 13 Indoor Bike Specialties',
    otherTradesItems: [
      'Blocking',
      'If building is constructed with post tension rods, x-ray by others required before installation'
    ],
    scopeConcerns: [
      'Confirm color'
    ],
    fieldMeasurementItems: [
      'No field measurements are performed on this scope; if needed, trip charge may apply'
    ],
    installationItems: [
      'Has final paint been completed'
    ],
    isBuiltIn: true
  },
  'commercial-glass': {
    name: 'Commercial Glass',
    specSection: '08 40 00 Storefront/Entrances/Glazing',
    otherTradesItems: [
      'Wall openings',
      'Blocking',
      'Confirm there is no radiant flooring to coordinate floor mounting'
    ],
    scopeConcerns: [],
    fieldMeasurementItems: [
      'Confirm date when all areas can be field measured by ABS'
    ],
    installationItems: [
      'Confirm attic stock requirements per spec'
    ],
    isBuiltIn: true
  },
  'fire-extinguishers': {
    name: 'Fire Protection',
    specSection: '10 44 16 Fire Extinguishers',
    otherTradesItems: [
      'Wall openings',
      'Blocking',
      'Confirm cabinet will fit in wall depth (depth info is provided in this submittal)',
      'Caulking and/or backer rod'
    ],
    scopeConcerns: [
      'All extinguishers are certified "new" out of the box, and do not require recertifications unless required by local inspector. If recertifications are required, this is excluded from Y/N'
    ],
    fieldMeasurementItems: [
      'No field measurements are performed on this scope; if needed, trip charge may apply'
    ],
    installationItems: [
      'Has final paint been completed'
    ],
    isBuiltIn: true
  },
  'frp': {
    name: 'FRP',
    specSection: '09 77 30 Fiberglass Reinforced Polyester Panels',
    otherTradesItems: [
      'Walls to be primed',
      'Climate controlled building/rooms'
    ],
    scopeConcerns: [
      'Building temperatures must remain at or above 65° - if they fall below, ABS will pull off of project. Additional trip charges may apply',
      'Color selections confirmed',
      'Sheet goods must be installed prior to sinks/plumbing fixtures/handles/large equipment',
      'If floor to ceiling installation, sheet goods should be installed to ceiling grid/tile',
      'Confirm trim requirements',
      'Identify CMU locations - different adhesive will be required'
    ],
    fieldMeasurementItems: [
      'No field measurements are performed on this scope; if needed, trip charge may apply'
    ],
    installationItems: [
      'Surfaces must be taped, mudded, primed and smooth',
      'Confirm no cabinets or other equipment has been installed',
      'Schluter trim, tile and covebase must be installed',
      'Screw inspections must be completed prior to installation of FRP'
    ],
    isBuiltIn: true
  },
  'lockers': {
    name: 'Lockers',
    specSection: '10 51 00 Lockers',
    otherTradesItems: [
      'Floor must be level',
      'Blocking for wall-mounted units'
    ],
    scopeConcerns: [
      'Verify locker configuration',
      'Confirm numbering sequence'
    ],
    fieldMeasurementItems: [
      'Room dimensions verified',
      'Electrical locations (if powered)'
    ],
    installationItems: [
      'Flooring complete',
      'Walls painted'
    ],
    isBuiltIn: true
  },
  'mirrors': {
    name: 'Mirrors',
    specSection: '10 28 01 Mirrors',
    otherTradesItems: [
      'Wall openings',
      'Blocking',
      'Identify wainscot heights'
    ],
    scopeConcerns: [
      'Confirm large frameless mirrors are by Division 08 Y/N'
    ],
    fieldMeasurementItems: [
      'Drywall completed',
      'Wall cutouts completed'
    ],
    installationItems: [
      'Is there enough room between mirrors for soap dispensers?',
      'Are countertops in place?'
    ],
    isBuiltIn: true
  },
  'operable-wall': {
    name: 'Operable Partitions',
    specSection: '10 22 39 Operable Partitions',
    otherTradesItems: [
      'Wall openings',
      'Blocking',
      'Structural steel support required by others',
      'Confirm color'
    ],
    scopeConcerns: [],
    fieldMeasurementItems: [
      'Field measurements for panels will be taken by ABS at the time of track install'
    ],
    installationItems: [
      'Confirm no M.E.P conflicts exist with op wall track',
      'Panels may be shipped to job site. Please advise ABS if there will be access issues with getting the truck in or out of the parking lot. Additionally we will also need to have sidewalks poured and the asphalt laid to roll our carts over. Please advise if these requirements present any logistical problems'
    ],
    isBuiltIn: true
  },
  'operable-wall-crown-door': {
    name: 'Operable Partitions - Crown Door',
    specSection: '10 22 26 Operable Partitions',
    otherTradesItems: [
      'Wall openings',
      'Blocking',
      'Structural steel support required by others',
      'Confirm finish colors'
    ],
    scopeConcerns: [],
    fieldMeasurementItems: [
      'Field conditions will supersede shop drawings, field dimensions are necessary for manufacturing precision. In the event that conditions change notify ABS',
      'Confirm date when all areas can be field measured by ABS, this will be completed with installation of track; any additional trips will be an added trip charge'
    ],
    installationItems: [
      'Confirm no M.E.P conflicts exist with op wall track'
    ],
    isBuiltIn: true
  },
  'operable-wall-unispan': {
    name: 'Operable Partitions - UNISPAN',
    specSection: '10 22 26 Operable Partitions',
    otherTradesItems: [
      'Wall openings',
      'Blocking',
      'Select vinyl, marker board colors and trim colors'
    ],
    scopeConcerns: [],
    fieldMeasurementItems: [
      'Confirm date when all areas can be field measured by ABS, this will be completed with installation of track; any additional trips will be an added trip charge'
    ],
    installationItems: [
      'Confirm no M.E.P. conflicts exist with op wall track',
      'Unispan posts will be installed outside finished walls, have walls prepared and final painted before delivery of Unispan'
    ],
    isBuiltIn: true
  },
  'postal': {
    name: 'Postal Specialties',
    specSection: '10 55 00 Postal Specialties',
    otherTradesItems: [
      'Wall openings',
      'Blocking'
    ],
    scopeConcerns: [
      'Color selected',
      'Provide number sequencing'
    ],
    fieldMeasurementItems: [
      'Drywall completed',
      'Wall cutouts completed'
    ],
    installationItems: [
      'Has final paint been completed'
    ],
    isBuiltIn: true
  },
  'site-furnishings': {
    name: 'Site Furnishings',
    specSection: '12 93 00 Site Furnishings',
    otherTradesItems: [
      'Wall openings',
      'Blocking',
      'Confirm color'
    ],
    scopeConcerns: [],
    fieldMeasurementItems: [],
    installationItems: [],
    isBuiltIn: true
  },
  'smoke-fire-curtains': {
    name: 'Smoke & Fire Curtains',
    specSection: '08 33 43 Overhead Coiling Smoke Curtains for Elevator Doors',
    otherTradesItems: [
      'Wall openings',
      'Blocking',
      '75% material deposit required before material can be released'
    ],
    scopeConcerns: [],
    fieldMeasurementItems: [
      'No field measurements are performed on this scope; if needed, trip charge may apply'
    ],
    installationItems: [
      'Has final paint been completed',
      'Confirm attic stock requirements per spec'
    ],
    isBuiltIn: true
  },
  'toilet-accessories': {
    name: 'Toilet Accessories',
    specSection: '10 28 00 Toilet Accessories',
    otherTradesItems: [
      'Blocking',
      'All grab bar locations to have blocking'
    ],
    scopeConcerns: [
      'Verify ADA compliance requirements',
      'Confirm mounting heights with architect'
    ],
    fieldMeasurementItems: [
      'Partition installation complete',
      'Fixture rough-in complete'
    ],
    installationItems: [
      'Walls must be finished',
      'Tile work complete in wet areas'
    ],
    isBuiltIn: true
  },
  'toilet-compartments': {
    name: 'Toilet Compartments',
    specSection: '10 21 13 Toilet Compartments & ADA Bench Items',
    otherTradesItems: [
      'Blocking',
      'Ceiling supports by others',
      'Confirm there is no radiant flooring to coordinate floor mounting with',
      'Toilet fixtures to be installed prior to partition installation'
    ],
    scopeConcerns: [
      'Confirm color selection',
      'Identify any elevations that require child height'
    ],
    fieldMeasurementItems: [
      'Framing and plumbing roughs set',
      'If floor to ceiling installation - wood backing is required (suggestion is 2x6 backing)',
      'If floor mounted partitions: confirm no radiant flooring in room',
      'If ceiling hung partitions: ABS must arrive prior to ceiling being furnished to install ceiling bolts, then ceiling must be finished prior to panel installation'
    ],
    installationItems: [
      'Permanent lighting required in restrooms prior to partitions installation (by others)',
      'Restrooms to be available exclusively to ABS',
      'Completion of wall & floor tile & grouting',
      'Wall must be finished 24-48 hours prior',
      'Final paint is complete',
      'All ceiling work is complete',
      'Countertops must be installed otherwise gaps may occur, ABS is not responsible if this condition is not met',
      'Confirm no adjoining products interfere with pilaster installation at partial height tile wainscot & continuous brackets, if not addressed prior, ABS will not fill gaps (by others)',
      'If tile is not fully covered with mortar/mastic, tiles will break when drilled. ABS is not responsible for broken tile'
    ],
    isBuiltIn: true
  },
  'wall-protection': {
    name: 'Wall Protection',
    specSection: '10 26 00 Wall & Door Protection',
    otherTradesItems: [
      'Blocking',
      'Handrail or any horizontal items must have continuous blocking',
      'Walls to be primed'
    ],
    scopeConcerns: [
      'All non-90 degree corners will require field measurements before material is ordered',
      'Fire rated corner guards are furnished only and must be installed by drywall contractor'
    ],
    fieldMeasurementItems: [
      'Completion of wainscot',
      'Colors have been selected - if applicable'
    ],
    installationItems: [
      "Handrail: provide dimensions at termination points. Door & window jamb, FEC's, corners, cased openings",
      'Corner guards: cove base must be in place',
      'Corner guards: walls must be painted',
      'Clear corner guards: recommendation is to be back painted to match walls (by others)'
    ],
    isBuiltIn: true
  },
  'wire-mesh-partitions': {
    name: 'Wire Mesh Partitions',
    specSection: '10 22 13 Wire Mesh Partitions',
    otherTradesItems: [
      'Blocking'
    ],
    scopeConcerns: [
      'Confirm there are no radiant floors to coordinate floor mounting with',
      'Confirm locker # sequence',
      'Select color',
      'Per NFP13: 18" vertical clearance without obstructions is required between the top of cage & ceilings. If shelves are on nearby wall, that is acceptable'
    ],
    fieldMeasurementItems: [
      'Walled cages: confirm max wall span dimensions',
      'Cages with roofs: confirm no ceiling cutouts required',
      'Max roof size is 4\'x4\' or additional structure/ceiling supports are required',
      'Roofs cannot be used with columns'
    ],
    installationItems: [
      'Confirm attic stock requirements per spec'
    ],
    isBuiltIn: true
  }
};

// Editable List Section Component
function EditableListSection({ title, items, setItems, bgColor = '', showYesNoSelector = false, yesNoValue, onYesNoChange }) {
  const addItem = () => setItems([...items, '']);
  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    setItems(newItems);
  };
  const removeItem = (index) => {
    if (items.length > 1) setItems(items.filter((_, i) => i !== index));
  };

  // Hide items when Y/N selector is present and value is "N"
  const showItems = !showYesNoSelector || yesNoValue !== 'N';

  return (
    <div className={`p-4 border-b border-gray-300 ${bgColor}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Label className="font-bold text-gray-900">{title}</Label>
          {showYesNoSelector && (
            <select
              value={yesNoValue || ''}
              onChange={(e) => onYesNoChange && onYesNoChange(e.target.value)}
              className="h-7 px-2 text-sm border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/50"
              data-testid="field-measurements-yn-selector"
            >
              <option value="">Select...</option>
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
          )}
        </div>
        {showItems && (
          <Button type="button" variant="outline" size="sm" onClick={addItem} className="h-7 text-xs">
            <Plus className="h-3 w-3 mr-1" /> Add
          </Button>
        )}
      </div>
      {showItems && (
        <div className="space-y-2 ml-4">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 group">
              <span className="text-gray-600">-</span>
              <Input 
                value={item}
                onChange={(e) => updateItem(idx, e.target.value)}
                className="flex-1 h-8 text-sm text-gray-900"
                placeholder="Enter item..."
              />
              <Button 
                type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)}
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50"
                disabled={items.length <= 1}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      {!showItems && (
        <p className="text-sm text-gray-500 italic ml-4">Not applicable when "No" is selected</p>
      )}
    </div>
  );
}

export default function TemplatesStep({ project, onComplete, selectedTemplates, setSelectedTemplates, usedTemplateIds = [], standaloneMode = false, onBack }) {
  // All templates (built-in + custom)
  const [allTemplates, setAllTemplates] = useState({ ...BUILTIN_SCOPE_TEMPLATES });
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Template editing state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editMode, setEditMode] = useState(null); // 'view' | 'edit' | 'duplicate'
  
  // Edit form state
  const [templateName, setTemplateName] = useState('');
  const [specSection, setSpecSection] = useState('');
  const [otherTradesItems, setOtherTradesItems] = useState(['']);
  const [scopeConcerns, setScopeConcerns] = useState(['']);
  const [fieldMeasurementItems, setFieldMeasurementItems] = useState(['']);
  const [fieldMeasurementRequired, setFieldMeasurementRequired] = useState('');
  const [installationItems, setInstallationItems] = useState(['']);
  const [installationPrepRequired, setInstallationPrepRequired] = useState('');
  const [atticStockRequired, setAtticStockRequired] = useState('');
  const [atticStockItems, setAtticStockItems] = useState(['']);
  const [leadTime, setLeadTime] = useState('');
  const [shippingTime, setShippingTime] = useState('');
  const [installDuration, setInstallDuration] = useState('');
  const [returnSubmittalDue, setReturnSubmittalDue] = useState('');
  const [planDate, setPlanDate] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [itemsAttached, setItemsAttached] = useState({
    product_data: true, shop_drawings: true, color_selectors: true,
    leed_docs: false, warranty_samples: false, quantity_takeoff: false,
    msds_sheets: false, other1_checked: false, other1_text: '',
    other2_checked: false, other2_text: ''
  });
  
  // Save dialog
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Smart FRS Upload state
  const [smartUploadFiles, setSmartUploadFiles] = useState([]);
  const [processingSmartUpload, setProcessingSmartUpload] = useState(false);
  const [matchedTemplates, setMatchedTemplates] = useState([]);
  const [showSmartUploadResults, setShowSmartUploadResults] = useState(false);
  
  // Smart Proposal Upload state
  const [uploadingProposal, setUploadingProposal] = useState(false);
  const [proposalParseResult, setProposalParseResult] = useState(null);

  // Load custom templates on mount
  useEffect(() => {
    loadCustomTemplates();
  }, []);

  const loadCustomTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/scope-templates`);
      if (response.data.templates) {
        const allDbTemplates = response.data.templates;
        
        // Separate hidden markers from real templates
        const hiddenKeys = new Set(
          allDbTemplates.filter(t => t.hidden).map(t => t.id)
        );
        const visibleCustom = allDbTemplates.filter(t => !t.hidden);
        
        setCustomTemplates(visibleCustom);
        // Merge custom templates into allTemplates
        const customObj = {};
        visibleCustom.forEach(t => {
          // If this template overrides a built-in, use the built-in key
          const key = t.overridesBuiltIn || t.id;
          customObj[key] = {
            id: t.id,
            name: t.name,
            specSection: t.specSection,
            otherTradesItems: t.otherTradesItems || [],
            scopeConcerns: t.scopeConcerns || [],
            fieldMeasurementItems: t.fieldMeasurementItems || [],
            fieldMeasurementRequired: t.fieldMeasurementRequired || '',
            installationItems: t.installationItems || [],
            installationPrepRequired: t.installationPrepRequired || '',
            atticStockRequired: t.atticStockRequired || '',
            atticStockItems: t.atticStockItems || [],
            itemsAttached: t.itemsAttached || null,
            isCustom: true,
            overridesBuiltIn: t.overridesBuiltIn || null
          };
        });
        
        // Start with built-in, exclude hidden ones
        const merged = {};
        Object.keys(BUILTIN_SCOPE_TEMPLATES).forEach(key => {
          if (!hiddenKeys.has(key)) {
            merged[key] = BUILTIN_SCOPE_TEMPLATES[key];
          }
        });
        // Custom overrides take precedence (and may also be hidden)
        Object.keys(customObj).forEach(key => {
          if (!hiddenKeys.has(key)) {
            merged[key] = customObj[key];
          }
        });
        
        setAllTemplates(merged);
      }
    } catch (error) {
      console.error('Error loading custom templates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Local checkbox state - only pushed to parent on "Add to Submittals" click
  const [localChecked, setLocalChecked] = useState([]);

  const handleTemplateToggle = (templateId) => {
    setLocalChecked(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(t => t !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  // Smart FRS Upload - Match filenames to templates
  const matchFRSToTemplates = useCallback((files) => {
    const templatesList = Object.entries(allTemplates).map(([id, template]) => ({
      id,
      name: template.name,
      specSection: template.specSection,
      keywords: [
        template.name.toLowerCase(),
        ...template.name.toLowerCase().split(' '),
        template.specSection?.toLowerCase() || ''
      ]
    }));

    const matches = [];
    
    files.forEach(file => {
      const filename = file.name.toLowerCase().replace(/\.[^/.]+$/, ''); // Remove extension
      const words = filename.split(/[\s_\-\.]+/); // Split by spaces, underscores, dashes, dots
      
      let bestMatch = null;
      let bestScore = 0;
      
      templatesList.forEach(template => {
        let score = 0;
        
        // Check for exact name match
        if (filename.includes(template.name.toLowerCase().replace(/\s+/g, ''))) {
          score += 10;
        }
        
        // Check for keyword matches
        template.keywords.forEach(keyword => {
          if (keyword && filename.includes(keyword)) {
            score += 3;
          }
          words.forEach(word => {
            if (word.length > 2 && keyword && keyword.includes(word)) {
              score += 1;
            }
          });
        });
        
        // Special matching for common terms
        const commonMatches = {
          'locker': ['awl-lockers', 'lockers'],
          'wall': ['wall-protection'],
          'protection': ['wall-protection'],
          'mirror': ['mirrors'],
          'glass': ['commercial-glass'],
          'toilet': ['toilet-partitions', 'toilet-accessories'],
          'partition': ['toilet-partitions', 'wire-mesh-partitions'],
          'cabinet': ['aed-cabinets'],
          'aed': ['aed-cabinets'],
          'fire': ['fire-extinguisher', 'smoke-fire-curtains'],
          'extinguisher': ['fire-extinguisher'],
          'mailbox': ['mailboxes'],
          'signage': ['signage'],
          'sign': ['signage'],
          'marker': ['markerboards'],
          'whiteboard': ['markerboards'],
          'corner': ['corner-guards'],
          'guard': ['corner-guards'],
          'wire': ['wire-mesh-partitions'],
          'mesh': ['wire-mesh-partitions'],
          'smoke': ['smoke-fire-curtains'],
          'curtain': ['smoke-fire-curtains']
        };
        
        words.forEach(word => {
          if (commonMatches[word]) {
            commonMatches[word].forEach(matchId => {
              if (template.id === matchId) {
                score += 5;
              }
            });
          }
        });
        
        if (score > bestScore) {
          bestScore = score;
          bestMatch = template;
        }
      });
      
      matches.push({
        file,
        matchedTemplate: bestMatch,
        confidence: bestScore > 5 ? 'high' : bestScore > 2 ? 'medium' : 'low',
        score: bestScore
      });
    });
    
    return matches;
  }, [allTemplates]);

  // Acronym-to-template mapping for FRS tab matching
  // Keys: FRS tab acronym (lowercase), Values: template IDs or name fragments to match
  const SCOPE_ACRONYMS = {
    'bike': ['bike-racks'],
    'cct': ['cubicle-curtain'],
    'cg': ['corner-guards'],
    'chute': ['trash-chutes'],
    'em': ['entrance-mats'],
    'ent': ['entrance-mats'],
    'flag': ['flag-poles'],
    'fps': ['fire-extinguishers', 'fire-protection'],
    'frp': ['frp'],
    'ghdwr': ['glass-hardware', 'commercial-glass'],
    'glass': ['commercial-glass', 'glazing'],
    'glaz': ['commercial-glass', 'glazing'],
    'gym': ['gym-equipment'],
    'lkr': ['awl-lockers', 'lockers'],
    'locker': ['awl-lockers', 'lockers'],
    'mir': ['mirrors'],
    'misc': ['miscellaneous'],
    'ohd': ['overhead-doors'],
    'opw': ['operable-wall', 'operable-wall-crown-door', 'operable-wall-unispan'],
    'photo': ['photoluminescent'],
    'post': ['postal'],
    'sc': ['smoke-fire-curtains'],
    'score': ['scoreboards'],
    'shc': ['shower-surrounds'],
    'shd': ['shower-doors'],
    'site': ['site-furnishings'],
    'smoke': ['smoke-fire-curtains'],
    'sun': ['sun-control'],
    'tc': ['toilet-compartments'],
    'tra': ['toilet-accessories'],
    'trash': ['trash-compactors'],
    'vdu': ['visual-display'],
    'wmp': ['wire-mesh-partitions'],
    'wp': ['wall-protection'],
  };

  // Trade name mapping for matching backend trade_name to template names
  const TRADE_NAME_KEYWORDS = {
    'bike racks': ['bike-racks'],
    'indoor bike racks': ['bike-racks'],
    'cubicle curtain and track': ['cubicle-curtain'],
    'corner guards': ['corner-guards'],
    'trash chutes': ['trash-chutes'],
    'entrance mats': ['entrance-mats'],
    'flag poles': ['flag-poles'],
    'fire protection specialties': ['fire-extinguishers'],
    'fire protection': ['fire-extinguishers'],
    'fiberglass reinforced panels': ['frp'],
    'glazing': ['commercial-glass'],
    'gym equipment': ['gym-equipment'],
    'lockers': ['awl-lockers', 'lockers'],
    'mirrors': ['mirrors'],
    'operable partitions': ['operable-wall', 'operable-wall-crown-door', 'operable-wall-unispan'],
    'photoluminescent markings': ['photoluminescent'],
    'postal specialties': ['postal'],
    'smoke curtains': ['smoke-fire-curtains'],
    'shower surrounds': ['shower-surrounds'],
    'shower doors': ['shower-doors'],
    'site furnishings': ['site-furnishings'],
    'toilet compartments': ['toilet-compartments'],
    'toilet room accessories': ['toilet-accessories'],
    'trash compactors': ['trash-compactors'],
    'visual display units': ['visual-display'],
    'wire mesh partitions': ['wire-mesh-partitions'],
    'wall protection': ['wall-protection'],
    'frp': ['frp'],
    'smoke & fire curtains': ['smoke-fire-curtains'],
    'overhead doors': ['overhead-doors'],
    'commercial glass': ['commercial-glass'],
    'glass hardware': ['glass-hardware', 'commercial-glass'],
    'sun control devices': ['sun-control'],
    'scoreboards': ['scoreboards'],
  };

  const matchScopesToTemplates = useCallback((scopes) => {
    const templatesList = Object.entries(allTemplates).map(([id, template]) => ({
      id,
      name: template.name,
      specSection: template.specSection,
      nameLower: template.name.toLowerCase(),
      specLower: (template.specSection || '').toLowerCase()
    }));

    const matches = [];
    scopes.forEach(scope => {
      const specLower = (scope.spec_section || '').toLowerCase();
      const tabLower = (scope.tab_name || '').toLowerCase().trim();
      const tradeLower = (scope.trade_name || '').toLowerCase().trim();
      const scopeNameLower = (scope.scope_name || '').toLowerCase().trim();
      const specNum = specLower.replace(/[^0-9]/g, '').substring(0, 6);

      let bestMatch = null;
      let bestScore = 0;

      templatesList.forEach(template => {
        let score = 0;
        const tmplSpecNum = template.specLower.replace(/[^0-9]/g, '').substring(0, 6);

        // Exact spec section number match (strongest)
        if (specNum && tmplSpecNum && specNum === tmplSpecNum) score += 20;

        // Acronym matching (tab name is the FRS acronym like "FPS", "WP")
        if (SCOPE_ACRONYMS[tabLower]) {
          SCOPE_ACRONYMS[tabLower].forEach(matchId => {
            if (template.id === matchId || template.nameLower.includes(matchId.replace(/-/g, ' '))) score += 15;
          });
        }

        // Trade name matching (backend resolves acronym to full trade name)
        if (tradeLower && TRADE_NAME_KEYWORDS[tradeLower]) {
          TRADE_NAME_KEYWORDS[tradeLower].forEach(matchId => {
            if (template.id === matchId || template.nameLower.includes(matchId.replace(/-/g, ' '))) score += 15;
          });
        }

        // Scope name / trade name word matching against template name
        [tradeLower, scopeNameLower].forEach(nameStr => {
          if (nameStr && nameStr.length > 2) {
            const words = nameStr.split(/[\s_\-]+/);
            const nameWords = template.nameLower.split(/[\s_\-]+/);
            words.forEach(tw => {
              if (tw.length > 2) {
                nameWords.forEach(nw => {
                  if (nw.length > 2 && (nw.includes(tw) || tw.includes(nw))) score += 5;
                });
              }
            });
          }
        });

        // Spec section text partial match (for new FRS files with full spec names)
        if (specLower.length > 3 && template.specLower.length > 3) {
          const specWords = specLower.split(/[\s]+/).filter(w => w.length > 2 && !/^\d+$/.test(w));
          specWords.forEach(sw => {
            if (template.nameLower.includes(sw) || template.specLower.includes(sw)) score += 8;
          });
        }

        if (score > bestScore) { bestScore = score; bestMatch = template; }
      });

      matches.push({
        scope,
        matchedTemplate: bestMatch,
        confidence: bestScore >= 15 ? 'high' : bestScore >= 5 ? 'medium' : 'low',
        score: bestScore
      });
    });
    return matches;
  }, [allTemplates]);

  const handleSmartUpload = useCallback(async (files) => {
    setProcessingSmartUpload(true);
    setSmartUploadFiles(files);
    
    try {
      // For Excel files, parse tabs via backend for scope matching
      const excelFiles = files.filter(f => f.name.match(/\.xlsx?$/i));
      
      if (excelFiles.length > 0) {
        const formData = new FormData();
        formData.append('file', excelFiles[0]);
        const token = localStorage.getItem('auth_token');
        
        const previewRes = await axios.post(`${API}/upload/frs-preview`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
        });
        
        if (previewRes.data.success && previewRes.data.scopes?.length > 0) {
          const scopeMatches = matchScopesToTemplates(previewRes.data.scopes);
          setMatchedTemplates(scopeMatches.map(m => ({
            file: excelFiles[0],
            matchedTemplate: m.matchedTemplate,
            confidence: m.confidence,
            score: m.score,
            scope: m.scope
          })));
          
          const templatesToSelect = scopeMatches
            .filter(m => m.matchedTemplate && m.confidence !== 'low')
            .map(m => m.matchedTemplate.id);
          
          if (templatesToSelect.length > 0) {
            setSelectedTemplates(prev => [...new Set([...prev, ...templatesToSelect])]);
            setLocalChecked(prev => [...new Set([...prev, ...templatesToSelect])]);
            toast.success(`Matched ${templatesToSelect.length} of ${previewRes.data.scopes.length} FRS scopes to templates`);
          } else {
            toast.info('No automatic matches found. Please select templates manually.');
          }
          
          setShowSmartUploadResults(true);
          return;
        }
      }
      
      // Fallback: filename-based matching for non-Excel files
      const matches = matchFRSToTemplates(files);
      setMatchedTemplates(matches);
      
      const templatesToSelect = matches
        .filter(m => m.matchedTemplate && m.confidence !== 'low')
        .map(m => m.matchedTemplate.id);
      
      if (templatesToSelect.length > 0) {
        setSelectedTemplates(prev => [...new Set([...prev, ...templatesToSelect])]);
        setLocalChecked(prev => [...new Set([...prev, ...templatesToSelect])]);
        toast.success(`Matched ${templatesToSelect.length} template(s) from ${files.length} FRS file(s)`);
      } else {
        toast.info('No automatic matches found. Please select templates manually.');
      }
      
      setShowSmartUploadResults(true);
    } catch (error) {
      console.error('Smart upload error:', error);
      toast.error('Failed to process files');
    } finally {
      setProcessingSmartUpload(false);
    }
  }, [matchFRSToTemplates, matchScopesToTemplates, setSelectedTemplates]);

  // Dropzone for smart FRS upload
  const onSmartDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      handleSmartUpload(acceptedFiles);
    }
  }, [handleSmartUpload]);

  const { getRootProps: getSmartDropRootProps, getInputProps: getSmartDropInputProps, isDragActive: isSmartDragActive } = useDropzone({
    onDrop: onSmartDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'application/pdf': ['.pdf']
    },
    multiple: true,
    disabled: processingSmartUpload
  });

  // Smart Proposal Upload - Parse proposal and auto-create submittals with exclusions
  const handleProposalUpload = useCallback(async (file) => {
    if (!file || !project?.id) {
      toast.error('Please select a project first');
      return;
    }
    
    setUploadingProposal(true);
    setProposalParseResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/projects/${project.id}/parse-proposal-and-create-submittals`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        const result = response.data;
        setProposalParseResult(result);
        
        if (result.submittals_created && result.submittals_created.length > 0) {
          toast.success(`Created ${result.submittals_created.length} submittal(s) from proposal with exclusions!`);
          
          // Trigger navigation to Submittals tab after a brief delay
          setTimeout(() => {
            if (onComplete) {
              onComplete([]); // Navigate to submittals without adding more templates
            }
          }, 1500);
        } else if (result.scopes_found.length === 0) {
          toast.warning('No scopes found in the proposal');
        } else {
          toast.info(`Found ${result.scopes_found.length} scope(s), but all already exist or couldn't be matched`);
        }
      }
    } catch (error) {
      console.error('Error parsing proposal:', error);
      const errMsg = error.response?.data?.detail || 'Failed to parse proposal';
      toast.error(errMsg);
    } finally {
      setUploadingProposal(false);
    }
  }, [project?.id, onComplete]);

  // Dropzone for proposal upload
  const onProposalDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      handleProposalUpload(acceptedFiles[0]);
    }
  }, [project?.id, handleProposalUpload]);

  const { getRootProps: getProposalDropRootProps, getInputProps: getProposalDropInputProps, isDragActive: isProposalDragActive } = useDropzone({
    onDrop: onProposalDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    disabled: uploadingProposal || !project?.id,
    noClick: false
  });

  const handleOpenTemplate = (templateId, mode = 'view') => {
    const template = allTemplates[templateId];
    if (template) {
      setEditingTemplate(templateId);
      setEditMode(mode);
      setTemplateName(mode === 'duplicate' ? `${template.name} (Copy)` : template.name);
      setSpecSection(template.specSection);
      setOtherTradesItems([...(template.otherTradesItems || [''])]);
      setScopeConcerns([...(template.scopeConcerns || [''])]);
      setFieldMeasurementItems([...(template.fieldMeasurementItems || [''])]);
      setFieldMeasurementRequired(template.fieldMeasurementRequired || '');
      setInstallationItems([...(template.installationItems || [''])]);
      setInstallationPrepRequired(template.installationPrepRequired || '');
      setAtticStockRequired(template.atticStockRequired || '');
      setAtticStockItems([...(template.atticStockItems || [''])]);
      setLeadTime(template.leadTime || '');
      setShippingTime(template.shippingTime || '');
      setInstallDuration(template.installDuration || '');
      setReturnSubmittalDue(template.returnSubmittalDue || '');
      setPlanDate(template.planDate || '');
      setAdditionalInfo(template.additionalInfo || '');
      setItemsAttached(template.itemsAttached || {
        product_data: true, shop_drawings: true, color_selectors: true,
        leed_docs: false, warranty_samples: false, quantity_takeoff: false,
        msds_sheets: false, other1_checked: false, other1_text: '',
        other2_checked: false, other2_text: ''
      });
    }
  };

  // Command palette quick action: open new-template editor
  useEffect(() => {
    const openNew = () => handleCreateNewTemplate();
    window.addEventListener('sb:new-template', openNew);
    return () => window.removeEventListener('sb:new-template', openNew);
     
  }, []);

  // Command palette: open a specific template (waits for templates to load)
  const pendingOpenTemplateRef = useRef(null);
  const [openTemplateTick, setOpenTemplateTick] = useState(0);
  useEffect(() => {
    const openTpl = (e) => {
      pendingOpenTemplateRef.current = e.detail;
      setOpenTemplateTick(t => t + 1);
    };
    window.addEventListener('sb:open-template', openTpl);
    return () => window.removeEventListener('sb:open-template', openTpl);
  }, []);
  useEffect(() => {
    const id = pendingOpenTemplateRef.current;
    if (!id) return;
    // Resolve UUID to allTemplates key. Custom templates that override built-ins
    // are keyed by overridesBuiltIn (e.g. 'lockers') not by their UUID.
    let resolvedKey = null;
    if (allTemplates[id]) {
      resolvedKey = id;
    } else {
      for (const k of Object.keys(allTemplates)) {
        if (allTemplates[k] && allTemplates[k].id === id) { resolvedKey = k; break; }
      }
    }
    if (resolvedKey) {
      pendingOpenTemplateRef.current = null;
      handleOpenTemplate(resolvedKey, 'edit');
    }
     
  }, [allTemplates, openTemplateTick]);

  const handleCreateNewTemplate = () => {
    setEditingTemplate('new');
    setEditMode('edit');
    setTemplateName('');
    setSpecSection('');
    setOtherTradesItems(['']);
    setScopeConcerns(['']);
    setFieldMeasurementItems(['']);
    setFieldMeasurementRequired('');
    setInstallationItems(['']);
    setInstallationPrepRequired('');
    setAtticStockRequired('');
    setAtticStockItems(['']);
    setLeadTime('');
    setShippingTime('');
    setInstallDuration('');
    setReturnSubmittalDue('');
    setPlanDate('');
    setAdditionalInfo('');
    setItemsAttached({
      product_data: true, shop_drawings: true, color_selectors: true,
      leed_docs: false, warranty_samples: false, quantity_takeoff: false,
      msds_sheets: false, other1_checked: false, other1_text: '',
      other2_checked: false, other2_text: ''
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !specSection.trim()) {
      toast.error('Please enter template name and spec section');
      return;
    }

    setSaving(true);
    try {
      const templateData = {
        name: templateName.trim(),
        specSection: specSection.trim(),
        otherTradesItems: otherTradesItems.filter(i => i.trim()),
        scopeConcerns: scopeConcerns.filter(i => i.trim()),
        fieldMeasurementItems: fieldMeasurementItems.filter(i => i.trim()),
        fieldMeasurementRequired: fieldMeasurementRequired,
        installationItems: installationItems.filter(i => i.trim()),
        installationPrepRequired: installationPrepRequired,
        atticStockRequired: atticStockRequired,
        atticStockItems: atticStockItems.filter(i => i.trim()),
        leadTime: leadTime.trim(),
        shippingTime: shippingTime.trim(),
        installDuration: installDuration.trim(),
        returnSubmittalDue: returnSubmittalDue.trim(),
        planDate: planDate.trim(),
        additionalInfo: additionalInfo.trim(),
        itemsAttached: itemsAttached,
        isCustom: true,
        overridesBuiltIn: editMode === 'edit' && allTemplates[editingTemplate]?.isBuiltIn 
          ? editingTemplate 
          : (allTemplates[editingTemplate]?.overridesBuiltIn || null)
      };

      // If editing existing custom template, update it
      if (editingTemplate !== 'new' && editMode === 'edit' && allTemplates[editingTemplate]?.isCustom) {
        const actualId = allTemplates[editingTemplate].id || editingTemplate;
        await axios.put(`${API}/scope-templates/${actualId}`, templateData);
        toast.success(`Template "${templateName}" updated!`);
      } 
      // If editing a built-in template, create an override version
      else if (editMode === 'edit' && allTemplates[editingTemplate]?.isBuiltIn) {
        // Check if an override already exists
        const existingOverride = Object.values(allTemplates).find(
          t => t.overridesBuiltIn === editingTemplate && t.isCustom
        );
        
        if (existingOverride) {
          // Update existing override
          await axios.put(`${API}/scope-templates/${existingOverride.id}`, templateData);
          toast.success(`Template "${templateName}" updated!`);
        } else {
          // Create new override
          await axios.post(`${API}/scope-templates`, templateData);
          toast.success(`Built-in template "${templateName}" overridden with your changes!`);
        }
      } 
      else {
        // Create new template
        await axios.post(`${API}/scope-templates`, templateData);
        toast.success(`Template "${templateName}" saved!`);
      }

      await loadCustomTemplates();
      setEditingTemplate(null);
      setEditMode(null);
      setSaveDialogOpen(false);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    const template = allTemplates[templateId];
    if (!template) return;

    setDeleting(true);
    try {
      if (template.isCustom) {
        const actualId = template.id || templateId;
        await axios.delete(`${API}/scope-templates/${actualId}`);
      } else {
        await axios.post(`${API}/scope-templates/${templateId}/hide`);
      }
      toast.success(`"${template.name}" deleted from transmittal templates`);
      setDeleteConfirmOpen(false);
      await loadCustomTemplates();
      setSelectedTemplates(prev => prev.filter(t => t !== templateId));
      setLocalChecked(prev => prev.filter(t => t !== templateId));
      setEditingTemplate(null);
      setEditMode(null);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const handleContinue = () => {
    if (localChecked.length === 0) {
      toast.error('Please select at least one template');
      return;
    }
    const templatesToAdd = [...localChecked];
    setLocalChecked([]); // Clear local selection after adding
    // Pass the FRS file along with templates if one was uploaded for smart matching
    const frsFile = smartUploadFiles?.find(f => f.name.match(/\.xlsx?$/i)) || null;
    onComplete(templatesToAdd, frsFile);
  };

  // Get all templates as array, sorted alphabetically by name
  // In project mode (not standalone), filter out templates already in submittals
  // In project mode, templates already in submittals are shown with an "In use" badge
  const templatesList = Object.entries(allTemplates)
    .map(([key, template]) => ({
      ...template,
      id: key,  // Use the dictionary key as the ID, not the template's internal id
      isUsed: !standaloneMode && usedTemplateIds.includes(key)
    }))
    .sort((a, b) => (a.isUsed === b.isUsed ? a.name.localeCompare(b.name) : (a.isUsed ? 1 : -1)));

  // Count of templates already in submittals (for display)
  const usedTemplatesCount = usedTemplateIds.length;

  // In standalone mode, we don't separate built-in vs custom - all templates are equal
  // In project mode, we still show built-in templates for selection

  // Render template selection view
  if (!editingTemplate) {
    return (
      <div className="space-y-6">
        <Card className="bg-card border-border shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1 flex items-center gap-2">
                  <LayoutTemplate className="h-5 w-5 text-primary" />
                  {standaloneMode ? 'Manage Transmittal Templates' : 'Transmittal Templates'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {standaloneMode 
                    ? 'Add new templates or modify existing ones. Changes will apply to all future projects.'
                    : 'Select templates to use for this project.'}
                </p>
              </div>
              {standaloneMode && (
                <Button 
                  onClick={handleCreateNewTemplate}
                  className="gap-2"
                  data-testid="create-template-button"
                >
                  <Plus className="h-4 w-4" />
                  Add New Template
                </Button>
              )}
              {!standaloneMode && (
                <Button 
                  variant="outline"
                  onClick={handleCreateNewTemplate}
                  className="gap-2"
                  data-testid="create-template-button-project"
                >
                  <Plus className="h-4 w-4" />
                  New Template
                </Button>
              )}
            </div>

            {/* Smart FRS Upload - Only in project mode */}
            {!standaloneMode && (
              <div className="mb-6 flex flex-wrap gap-3">
                {/* FRS Upload Button */}
                <div
                  {...getSmartDropRootProps()}
                  className="inline-block"
                >
                  <input {...getSmartDropInputProps()} />
                  <Button
                    disabled={processingSmartUpload}
                    className={`
                      gap-2 transition-all duration-200
                      ${isSmartDragActive ? 'ring-2 ring-primary ring-offset-2 bg-primary/90 scale-105' : ''}
                      ${processingSmartUpload ? 'opacity-50' : ''}
                    `}
                    data-testid="frs-upload-btn"
                  >
                    {processingSmartUpload ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    FRS Upload
                  </Button>
                </div>

                {/* Proposal Upload Button */}
                <div
                  {...getProposalDropRootProps()}
                  className="inline-block"
                >
                  <input {...getProposalDropInputProps()} />
                  <Button
                    disabled={uploadingProposal || !project?.id}
                    className={`
                      gap-2 transition-all duration-200 bg-rose-600 hover:bg-rose-700
                      ${isProposalDragActive ? 'ring-2 ring-rose-500 ring-offset-2 bg-rose-700 scale-105' : ''}
                      ${uploadingProposal || !project?.id ? 'opacity-50' : ''}
                    `}
                    data-testid="proposal-upload-btn"
                  >
                    {uploadingProposal ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    Proposal Upload
                  </Button>
                </div>

                {/* Smart Upload Results */}
                {showSmartUploadResults && matchedTemplates.length > 0 && (
                  <div className="w-full mt-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-green-800 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Matched Templates ({matchedTemplates.filter(m => m.matchedTemplate).length}/{matchedTemplates.length} scopes)
                      </h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowSmartUploadResults(false)}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {matchedTemplates.map((match, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {match.scope ? `${match.scope.scope_name || match.scope.trade_name || match.scope.tab_name}` : match.file?.name}
                              {match.scope?.tab_name && match.scope.scope_name !== match.scope.tab_name && (
                                <span className="text-xs text-gray-400 ml-1">({match.scope.tab_name})</span>
                              )}
                            </p>
                            {match.matchedTemplate ? (
                              <p className="text-xs text-green-600">
                                → {match.matchedTemplate.name}
                              </p>
                            ) : (
                              <p className="text-xs text-amber-600">No match found - select manually</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Proposal Parse Results */}
                {proposalParseResult && (
                  <div className="w-full mt-2 p-4 bg-rose-50 border border-rose-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-rose-800 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Proposal Parsed
                      </h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setProposalParseResult(null)}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p className="text-rose-700">
                        <span className="font-medium">{proposalParseResult.scopes_found?.length || 0}</span> scope(s) identified
                      </p>
                      <p className="text-rose-700">
                        <span className="font-medium">{proposalParseResult.submittals_created?.length || 0}</span> submittal(s) created
                      </p>
                      <p className="text-rose-700">
                        <span className="font-medium">{proposalParseResult.total_exclusions_found || 0}</span> exclusion(s) extracted
                      </p>
                      {proposalParseResult.skipped_scopes?.length > 0 && (
                        <p className="text-amber-600 text-xs">
                          Skipped: {proposalParseResult.skipped_scopes.join(', ')}
                        </p>
                      )}
                      {proposalParseResult.unmatched_exclusions?.length > 0 && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                          <p className="font-medium text-amber-700 mb-1">Unmatched exclusions:</p>
                          <ul className="text-amber-600 space-y-1 max-h-24 overflow-y-auto">
                            {proposalParseResult.unmatched_exclusions.slice(0, 5).map((exc, i) => (
                              <li key={i} className="truncate">• {exc}</li>
                            ))}
                            {proposalParseResult.unmatched_exclusions.length > 5 && (
                              <li>...and {proposalParseResult.unmatched_exclusions.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* All Templates - Single List, Alphabetically Sorted */}
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                <FileStack className="h-4 w-4" />
                {standaloneMode ? (
                  `Templates (${templatesList.length})`
                ) : (
                  <>
                    Available Templates ({templatesList.length})
                    {usedTemplatesCount > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {usedTemplatesCount} already in submittals
                      </Badge>
                    )}
                  </>
                )}
              </h4>
              {!standaloneMode && templatesList.length === 0 && usedTemplatesCount > 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">All templates have been added to submittals</p>
                  <p className="text-sm mt-1">Go to the Submittals tab to manage them</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {templatesList.map(template => (
                  <div 
                    key={template.id}
                    className={`
                      flex items-center gap-3 p-4 rounded-lg border-2 transition-all overflow-hidden
                      ${!standaloneMode && localChecked.includes(template.id) 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'}
                    `}
                    data-testid={`template-${template.id}`}
                  >
                    {!standaloneMode && (
                      <Checkbox 
                        checked={localChecked.includes(template.id)}
                        onCheckedChange={() => handleTemplateToggle(template.id)}
                        className="h-5 w-5 flex-shrink-0"
                      />
                    )}
                    <div 
                      className="flex-1 min-w-0 cursor-pointer" 
                      onClick={() => standaloneMode ? handleOpenTemplate(template.id, 'edit') : handleTemplateToggle(template.id)}
                    >
                      <p className="font-medium text-foreground truncate">{template.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{template.specSection}</p>
                    </div>
                    {/* Edit button - only in standalone mode, no delete option */}
                    {standaloneMode && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenTemplate(template.id, 'edit');
                        }}
                        className="h-8 w-8 p-0 flex-shrink-0"
                        title="Edit template"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {/* In project mode, show only edit button */}
                    {!standaloneMode && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenTemplate(template.id, 'edit');
                        }}
                        className="h-8 w-8 p-0 flex-shrink-0"
                        title="Edit template"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Selection Summary & Add - Hide in standalone mode */}
            {!standaloneMode && (
              <div className="pt-4 border-t border-border flex items-center justify-between mt-6">
                <p className="text-sm text-muted-foreground">
                  {localChecked.length > 0 
                    ? `${localChecked.length} template(s) selected to add`
                    : 'Select templates to add to submittals'}
                </p>
                <Button 
                  onClick={handleContinue}
                  disabled={localChecked.length === 0}
                  className="btn-primary"
                  data-testid="add-to-submittals-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Submittals
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render template edit view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button 
          variant="outline" 
          onClick={() => {
            setEditingTemplate(null);
            setEditMode(null);
          }}
          data-testid="back-to-templates-button"
        >
          ← Back to Templates
        </Button>
        <div className="flex items-center gap-2">
          {editMode === 'view' && allTemplates[editingTemplate]?.isBuiltIn && (
            <span className="text-sm text-muted-foreground mr-2">Built-in templates are read-only</span>
          )}
          {(editMode === 'edit' || editMode === 'duplicate' || editingTemplate === 'new') && (
            <Button
              onClick={handleSaveTemplate}
              disabled={saving}
              className="btn-primary"
              data-testid="save-template-button"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editingTemplate === 'new' || editMode === 'duplicate' ? 'Save as New Template' : 'Save Template'}
                </>
              )}
            </Button>
          )}
          {editingTemplate !== 'new' && (
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(true)}
              className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
              data-testid="delete-template-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Template
            </Button>
          )}
          {editMode === 'view' && allTemplates[editingTemplate]?.isBuiltIn && (
            <Button
              onClick={() => handleOpenTemplate(editingTemplate, 'duplicate')}
              variant="outline"
            >
              <Copy className="h-4 w-4 mr-2" />
              Duplicate as Custom
            </Button>
          )}
        </div>
      </div>

      {/* Template Form */}
      <Card className="bg-white border-border shadow-sm">
        <CardContent className="p-6">
          {/* Name and Spec Section */}
          <div className="border rounded-lg overflow-hidden mb-6">
            <div className="bg-gray-100 px-4 py-3 border-b">
              <h3 className="font-semibold text-sm text-gray-800 tracking-wide">SCOPE / SPEC SECTION</h3>
            </div>
            <div className="p-4 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold text-gray-900">Scope</Label>
                  <Input 
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="mt-1 bg-white text-gray-900 border-gray-300"
                    placeholder="e.g., Wall Protection"
                    data-testid="template-scope-input"
                  />
                </div>
                <div>
                  <Label className="font-semibold text-gray-900">Spec Section</Label>
                  <Input 
                    value={specSection}
                    onChange={(e) => setSpecSection(e.target.value)}
                    className="mt-1 bg-white text-gray-900 border-gray-300"
                    placeholder="e.g., 10 26 00 WALL & DOOR PROTECTION"
                    data-testid="template-spec-section-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Scheduling defaults (appear on the transmittal PDF) */}
          <div className="mt-6 rounded-lg border border-border overflow-hidden" data-testid="template-scheduling-section">
            <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 bg-card">
              {[
                ['Lead Time', leadTime, setLeadTime, 'template-lead-time-input'],
                ['Shipping Time', shippingTime, setShippingTime, 'template-shipping-time-input'],
                ['Install Duration', installDuration, setInstallDuration, 'template-install-duration-input'],
                ['Return Submittal Due', returnSubmittalDue, setReturnSubmittalDue, 'template-return-due-input'],
                ['Plan Date', planDate, setPlanDate, 'template-plan-date-input'],
                ['Additional Info.', additionalInfo, setAdditionalInfo, 'template-additional-info-input'],
              ].map(([label, value, setter, tid]) => (
                <div key={tid}>
                  <Label className="text-xs font-semibold text-muted-foreground">{label}</Label>
                  <Input
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    placeholder="-"
                    className="mt-1 h-8 text-sm"
                    data-testid={tid}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Items Attached Section */}
          <div className="border rounded-lg overflow-hidden mt-6">
            <div className="bg-gray-100 px-4 py-3 border-b">
              <h3 className="font-semibold text-sm text-gray-800 tracking-wide">ITEMS ATTACHED</h3>
            </div>
            <div className="p-4 bg-white">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                {[
                  { key: 'product_data', label: 'Product Data' },
                  { key: 'shop_drawings', label: 'Shop Drawings' },
                  { key: 'warranty_samples', label: 'Warranty Samples' },
                  { key: 'leed_docs', label: 'LEED Docs' },
                  { key: 'color_selectors', label: 'Color Selectors' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={!!itemsAttached[key]}
                      onChange={(e) => setItemsAttached(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      data-testid={`items-attached-${key}`}
                    />
                    {label}
                  </label>
                ))}
                {/* Other 1 */}
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={!!itemsAttached.other1_checked}
                    onChange={(e) => setItemsAttached(prev => ({ ...prev, other1_checked: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                    data-testid="items-attached-other1"
                  />
                  Other:
                  <input
                    type="text"
                    value={itemsAttached.other1_text || ''}
                    onChange={(e) => setItemsAttached(prev => ({ ...prev, other1_text: e.target.value }))}
                    className="border-b border-gray-300 bg-transparent text-sm text-gray-900 focus:outline-none focus:border-blue-500 w-24 px-1"
                    placeholder="..."
                    data-testid="items-attached-other1-text"
                  />
                </label>
                {[
                  { key: 'quantity_takeoff', label: 'Quantity Takeoff' },
                  { key: 'msds_sheets', label: 'MSDS Sheets' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={!!itemsAttached[key]}
                      onChange={(e) => setItemsAttached(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      data-testid={`items-attached-${key}`}
                    />
                    {label}
                  </label>
                ))}
                {/* Other 2 */}
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={!!itemsAttached.other2_checked}
                    onChange={(e) => setItemsAttached(prev => ({ ...prev, other2_checked: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                    data-testid="items-attached-other2"
                  />
                  Other:
                  <input
                    type="text"
                    value={itemsAttached.other2_text || ''}
                    onChange={(e) => setItemsAttached(prev => ({ ...prev, other2_text: e.target.value }))}
                    className="border-b border-gray-300 bg-transparent text-sm text-gray-900 focus:outline-none focus:border-blue-500 w-24 px-1"
                    placeholder="..."
                    data-testid="items-attached-other2-text"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Template Sections */}
          <div className="border rounded-lg overflow-hidden mt-4">
            <EditableListSection
              title="THE FOLLOWING ARE INSTALLED BY OTHERS"
              items={otherTradesItems}
              setItems={setOtherTradesItems}
              bgColor="bg-amber-50"
            />
            <EditableListSection
              title="SCOPE SPECIFIC / PROJECT SPECIFIC CONCERNS"
              items={scopeConcerns}
              setItems={setScopeConcerns}
            />
            <EditableListSection
              title="FIELD MEASUREMENT PREP (Y/N)"
              items={fieldMeasurementItems}
              setItems={setFieldMeasurementItems}
              bgColor="bg-blue-50"
              showYesNoSelector={true}
              yesNoValue={fieldMeasurementRequired}
              onYesNoChange={setFieldMeasurementRequired}
            />
            <EditableListSection
              title="INSTALLATION PREP (Y/N)"
              items={installationItems}
              setItems={setInstallationItems}
              bgColor="bg-green-50"
              showYesNoSelector={true}
              yesNoValue={installationPrepRequired}
              onYesNoChange={setInstallationPrepRequired}
            />
            
            {/* Attic Stock Required Section - Now with editable items */}
            <EditableListSection
              title="ATTIC STOCK REQUIRED (Y/N)"
              items={atticStockItems}
              setItems={setAtticStockItems}
              bgColor="bg-purple-50"
              showYesNoSelector={true}
              yesNoValue={atticStockRequired}
              onYesNoChange={setAtticStockRequired}
            />
          </div>

          {/* Summary */}
          <div className="mt-4 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm font-medium text-foreground mb-2">Template Summary:</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-muted-foreground">
              <div>{otherTradesItems.filter(i => i.trim()).length} Other Trades items</div>
              <div>{scopeConcerns.filter(i => i.trim()).length} Scope Concerns</div>
              <div>{fieldMeasurementItems.filter(i => i.trim()).length} Field Measurements</div>
              <div>{installationItems.filter(i => i.trim()).length} Installation Prep items</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Template
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600 pt-2">
              Are you sure you want to delete <span className="font-semibold text-gray-900">"{templateName || allTemplates[editingTemplate]?.name}"</span>?
              <br /><br />
              This will only remove it from the transmittal templates list. Any existing submittals using this template will not be affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={deleting}
              data-testid="delete-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDeleteTemplate(editingTemplate)}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-button"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Yes, Delete Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
